import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  HandHeart, Quote, Image as ImageIcon, MessageSquare, MessageCircle,
  Heart, Calendar as CalIcon, UserPlus, Sparkles, Share2, Check,
} from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { key: "lifetime", label: "Lifetime" },
  { key: "ytd", label: "This year" },
  { key: "12mo", label: "12 months" },
];

/**
 * Each counter has its own icon + theological framing so the dashboard reads
 * as a record of how the Lord has moved through this member — not a vanity
 * score. The order intentionally puts "prayed with others" first, because
 * intercession is the spiritual heart of this community.
 */
const METRIC_CONFIG = [
  { key: "prayed_for_others", label: "Prayers prayed with others", icon: HandHeart, accent: "text-burgundy" },
  { key: "prayers_lifted", label: "Prayer requests lifted", icon: HandHeart, accent: "text-jjblue" },
  { key: "testimonies_shared", label: "Testimonies shared", icon: Quote, accent: "text-burgundy" },
  { key: "encouragements", label: "Encouragements written", icon: MessageCircle, accent: "text-jjblue" },
  { key: "memories_shared", label: "Memories on the wall", icon: ImageIcon, accent: "text-burgundy" },
  { key: "posts_shared", label: "Praises & verses posted", icon: MessageSquare, accent: "text-jjblue" },
  { key: "gatherings_attended", label: "Gatherings attended", icon: CalIcon, accent: "text-burgundy" },
  { key: "loves_given", label: "Hearts & Amens given", icon: Heart, accent: "text-jjblue" },
  { key: "invited", label: "Brothers & sisters brought in", icon: UserPlus, accent: "text-burgundy" },
];

/**
 * Year in the Lord — a member's personal spiritual ROI dashboard.
 * Pass `userId` to load another member's view (read-only); omit to load /me.
 * Pass `compact` for a smaller embed without share controls.
 */
export default function YearInTheLord({ userId, compact = false }) {
  const [period, setPeriod] = useState("lifetime");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = userId ? `/users/${userId}/year-in-the-lord` : "/me/year-in-the-lord";
    api.get(url, { params: { period } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, period]);

  const share = async () => {
    if (!data?.share_line) return;
    try {
      if (navigator.share) {
        await navigator.share({ text: data.share_line, title: "My walk with Jesus Just Us" });
      } else {
        await navigator.clipboard.writeText(data.share_line);
        toast.success("Copied — paste anywhere to share");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // User cancelled or clipboard blocked — silent.
    }
  };

  if (loading) {
    return (
      <div className="jj-card mt-6 p-6 sm:p-7" data-testid="yitl-loading">
        <div className="overline text-burgundy flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Your Walk</div>
        <div className="mt-3 text-sm text-[var(--jj-muted)]">Gathering the harvest…</div>
      </div>
    );
  }
  if (!data) return null;

  const counts = data.counts || {};
  // loves_given is intentionally lifetime-only (server doesn't window it), so
  // exclude it from the hero total on the YTD / 12-month tabs and tag the tile
  // as "lifetime" so the number doesn't look inflated.
  const totalKeys = Object.keys(counts).filter(
    (k) => period === "lifetime" || k !== "loves_given"
  );
  const total = totalKeys.reduce((a, k) => a + (Number(counts[k]) || 0), 0);
  const isMine = !userId;
  const firstName = (data.user?.name || "").split(" ")[0] || "Member";

  return (
    <section className="jj-card mt-6 p-6 sm:p-8 relative overflow-hidden" data-testid="yitl-card">
      {/* Decorative gradient ribbon */}
      <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gold/15 blur-2xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="overline text-burgundy flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Year in the Lord
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-jjblue mt-2 leading-tight">
              {isMine
                ? "What the Lord has done through you."
                : `${firstName}'s walk with the family.`}
            </h2>
            <p className="mt-2 text-sm text-[var(--jj-text-secondary)] max-w-md">
              {data.member_days > 0
                ? `${data.member_days} day${data.member_days === 1 ? "" : "s"} into the journey — every prayer prayed, every testimony shared, every gathering shown up to.`
                : "Just getting started. The seeds you plant today bear fruit for years."}
            </p>
          </div>

          {/* Period tabs */}
          <div className="inline-flex bg-cream-surface rounded-full p-1 text-xs" data-testid="yitl-period">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-full transition-colors ${period === p.key ? "bg-burgundy text-cream" : "text-jjblue/70 hover:text-burgundy"}`}
                data-testid={`yitl-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Hero stat — total acts of love */}
        <div className="mt-7 flex items-baseline gap-3 flex-wrap">
          <span className="font-serif text-6xl sm:text-7xl text-gold leading-none" data-testid="yitl-total">{total}</span>
          <span className="text-sm text-[var(--jj-muted)]">acts of love & worship · {data.label}</span>
        </div>

        {/* Counter grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {METRIC_CONFIG.map(({ key, label, icon: Icon, accent }) => {
            const isLifetimeOnly = key === "loves_given" && period !== "lifetime";
            return (
              <div
                key={key}
                className="bg-cream-surface rounded-xl p-4 flex items-center gap-3"
                data-testid={`yitl-metric-${key}`}
              >
                <span className={`h-9 w-9 rounded-full bg-white flex items-center justify-center ${accent}`}>
                  <Icon className="h-4 w-4" strokeWidth={1.7} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-2xl text-jjblue leading-none" data-testid={`yitl-metric-value-${key}`}>
                    {counts[key] ?? 0}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-[var(--jj-muted)] truncate">
                    {label}
                    {isLifetimeOnly && (
                      <span className="ml-1.5 text-[9px] normal-case tracking-normal text-burgundy/80 bg-burgundy/10 px-1.5 py-0.5 rounded-full">
                        lifetime
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Share box (only on your own dashboard) */}
        {isMine && !compact && total > 0 && (
          <div className="mt-6 jj-card !p-4 bg-gradient-to-r from-cream to-cream-surface flex items-center gap-4 flex-wrap" data-testid="yitl-share-box">
            <Quote className="h-5 w-5 text-burgundy shrink-0" strokeWidth={1.5} />
            <p className="flex-1 min-w-[14rem] text-sm text-jjblue italic leading-relaxed">
              &ldquo;{data.share_line}&rdquo;
            </p>
            <button
              onClick={share}
              className={`text-xs inline-flex items-center gap-1.5 !py-2 !px-4 ${copied ? "btn-gold" : "btn-primary"}`}
              data-testid="yitl-share-btn"
            >
              {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Share2 className="h-3.5 w-3.5" /> Share my walk</>}
            </button>
          </div>
        )}

        {total === 0 && (
          <div className="mt-6 text-center text-sm text-[var(--jj-muted)] py-4" data-testid="yitl-empty">
            Nothing here yet — but the Lord is just getting started. Pray for a brother, share a testimony,
            or invite someone in. Watch this dashboard fill up.
          </div>
        )}
      </div>
    </section>
  );
}
